import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { redirectAfterLogin } from '../../utils/postLoginRedirect';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import PublicSEO from '../../components/public/PublicSEO';
import './AuthPublic.css';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    marketingOptIn: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const googleBtnRef = useRef(null);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !googleBtnRef.current) return undefined;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      if (window.google?.accounts?.id && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (res) => {
            if (!res?.credential) return;
            setOauthLoading(true);
            try {
              const result = await loginWithGoogle(res.credential);
              if (result.success) {
                toast.success('Accesso effettuato con Google');
                redirectAfterLogin(result, navigate, location.state);
              } else {
                toast.error(result.message || 'Accesso fallito');
              }
            } catch {
              toast.error('Errore accesso con Google');
            } finally {
              setOauthLoading(false);
            }
          },
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signup_with',
          width: 320,
        });
      }
    };
    document.body.appendChild(script);
    return () => {
      script.remove();
    };
  }, [loginWithGoogle, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await register(formData);
      if (result.success) {
        if (result.requireEmailConfirmation) {
          toast.success(result.message || 'Controlla la tua email e clicca sul link per impostare la password.');
          navigate('/login', { replace: true, state: location.state });
        } else {
          toast.success('Registrazione completata!');
          redirectAfterLogin(result, navigate, location.state);
        }
      } else {
        setError(result.message || 'Registrazione fallita');
      }
    } finally {
      setLoading(false);
    }
  };

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="vc-auth">
      <PublicSEO
        title="Registrati"
        description="Crea il tuo account paziente su Virtual Clinic per prenotare visite mediche online e gestire la tua documentazione clinica."
        canonicalPath="/register"
        noIndex
      />
      <div className="vc-auth__card">
        <h1 className="vc-auth__title">Crea il tuo account</h1>
        <p className="vc-auth__lead">
          Registrati per prenotare visite e gestire appuntamenti e documenti.
          Riceverai un&apos;email con il link per impostare la password.
        </p>

        {error && (
          <div className="vc-auth__error" role="alert">
            {error}
            {error.includes('già registrata come paziente') && (
              <p style={{ margin: '0.5rem 0 0' }}>
                <Link to="/login" className="vc-auth__link">Accedi</Link>
                {' · '}
                <Link to="/forgot-password" className="vc-auth__link">Password dimenticata</Link>
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="vc-auth__field">
            <label htmlFor="vc-reg-nome">Nome</label>
            <div className="vc-auth__input-wrap">
              <span className="vc-auth__icon" aria-hidden="true"><Icon icon="mage:user" /></span>
              <input id="vc-reg-nome" type="text" className="vc-auth__input" autoComplete="given-name" value={formData.nome} onChange={setField('nome')} required />
            </div>
          </div>
          <div className="vc-auth__field">
            <label htmlFor="vc-reg-cognome">Cognome</label>
            <div className="vc-auth__input-wrap">
              <span className="vc-auth__icon" aria-hidden="true"><Icon icon="mage:user" /></span>
              <input id="vc-reg-cognome" type="text" className="vc-auth__input" autoComplete="family-name" value={formData.cognome} onChange={setField('cognome')} required />
            </div>
          </div>
          <div className="vc-auth__field">
            <label htmlFor="vc-reg-email">Email</label>
            <div className="vc-auth__input-wrap">
              <span className="vc-auth__icon" aria-hidden="true"><Icon icon="mage:email" /></span>
              <input id="vc-reg-email" type="email" className="vc-auth__input" autoComplete="email" value={formData.email} onChange={setField('email')} required />
            </div>
          </div>
          <div className="vc-auth__field">
            <label htmlFor="vc-reg-tel">Telefono</label>
            <div className="vc-auth__input-wrap">
              <span className="vc-auth__icon" aria-hidden="true"><Icon icon="solar:phone-outline" /></span>
              <input id="vc-reg-tel" type="tel" className="vc-auth__input" autoComplete="tel" value={formData.telefono} onChange={setField('telefono')} required />
            </div>
          </div>

          <label className="vc-auth__check">
            <input
              type="checkbox"
              checked={!!formData.marketingOptIn}
              onChange={setField('marketingOptIn')}
            />
            <span>
              Acconsento a ricevere comunicazioni informative e promozionali (opzionale).
              Posso revocare il consenso dal profilo.
            </span>
          </label>

          <button type="submit" className="vc-btn vc-btn-primary vc-auth__submit" disabled={loading}>
            {loading ? 'Registrazione in corso…' : 'Registrati'}
          </button>

          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
              <p className="vc-auth__lead" style={{ marginBottom: '0.75rem' }}>oppure</p>
              <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center' }} />
              {oauthLoading && <p className="vc-auth__lead">Accesso con Google…</p>}
            </div>
          )}
        </form>

        <p className="vc-auth__footer">
          Hai già un account? <Link to="/login" className="vc-auth__link">Accedi</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
